/**
 * Loads a workshop list in day blocks, one request per block, and hands back
 * everything loaded so far as a single list.
 *
 * The start day is fixed when the screen first appears and is part of the
 * cache key, so the blocks stay put however long the screen stays open. A
 * block that fails leaves the earlier ones in place and is retried by asking
 * for more again.
 */
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { viewerDate } from '../../../utils/viewer-date';

import { blockRange, dedupeById, nextBlockStart, type DayRange } from './workshop-blocks';

const STALE_5_MIN = 5 * 60_000;

export interface UseWorkshopBlocksOptions<T> {
  keyParts: readonly unknown[];
  fetchBlock: (range: DayRange) => Promise<T[]>;
  enabled: boolean;
}

export interface WorkshopBlocks<T> {
  workshops: T[];
  through: string;
  /** Whether to name the last day loaded: not while the first block is loading or has nothing. */
  showThrough: boolean;
  isLoading: boolean;
  isFirstLoadError: boolean;
  error: unknown;
  loadMore: () => void;
  isLoadingMore: boolean;
  isLoadMoreError: boolean;
  /** The days of the newest block, when it came back empty. */
  emptyBlock: DayRange | null;
}

export function useWorkshopBlocks<T extends { id: string }>({
  keyParts,
  fetchBlock,
  enabled,
}: UseWorkshopBlocksOptions<T>): WorkshopBlocks<T> {
  const [anchor] = useState(() => viewerDate(0));
  const query = useInfiniteQuery<T[], Error, InfiniteData<T[], string>, readonly unknown[], string>(
    {
      queryKey: [...keyParts, anchor],
      queryFn: ({ pageParam }) => fetchBlock(blockRange(anchor, pageParam)),
      initialPageParam: anchor,
      getNextPageParam: (_last, _all, lastParam) => nextBlockStart(anchor, lastParam),
      staleTime: STALE_5_MIN,
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  const { data } = query;
  const workshops = useMemo(() => dedupeById(data?.pages.flat() ?? []), [data]);
  const lastStart = data?.pageParams[data.pageParams.length - 1] ?? anchor;
  const lastBlock = blockRange(anchor, lastStart);
  const newestIsEmpty = (data?.pages.length ?? 0) > 1 && data?.pages.at(-1)?.length === 0;

  return {
    workshops,
    through: lastBlock.to,
    showThrough: workshops.length > 0 || (data?.pages.length ?? 0) > 1,
    isLoading: query.isLoading,
    isFirstLoadError: query.isError && !data,
    error: query.error,
    loadMore: () => {
      if (!query.isFetching) void query.fetchNextPage();
    },
    isLoadingMore: query.isFetchingNextPage,
    isLoadMoreError: query.isFetchNextPageError,
    emptyBlock: newestIsEmpty ? lastBlock : null,
  };
}
